import React from 'react';
import { useSite } from '../context/SiteContext';

/**
 * Site-context guard for create forms (QA #97).
 *
 * QA reported filling in an entire Training Course form and only discovering on
 * submit that no site was selected — the backend rejects the create with
 * "No active site selected. Please select a site from the site selector." and
 * every entered value was lost.
 *
 * This is the generic form of the fix already shipped for QA #63 in
 * pages/training/Certifications.js: require the site BEFORE the form is
 * reachable, so the work is never thrown away.
 *
 * Two deliberate design decisions:
 *
 * 1. CREATE is gated; EDIT is not. An existing record already carries its own
 *    site, and gating edit would lock users out of records belonging to other
 *    sites they can legitimately access. Certifications.js encodes the same
 *    asymmetry as `if (!cert && !activeSiteId)`.
 *
 * 2. It points at the EXISTING global site selector rather than embedding a
 *    second picker. A second site-selection surface is a new class of
 *    divergence, and the site must be set through SiteContext anyway — the
 *    server resolves it from the JWT/active-site context, so a form-local
 *    value would still fail on submit.
 *
 * This is a UX affordance ONLY. The backend guards stay exactly where they are;
 * a stale token or a direct API call still gets the 400. Never rely on UI-only
 * enforcement.
 */

/** True once site context has loaded and a site is active. */
export const useSiteRequired = () => {
  const { activeSiteId, initialized, availableSites, isCorpUser } = useSite();
  return {
    /** Site context has finished loading — gate rendering on this to avoid a
     *  flash for single-site users, whom SiteContext auto-selects a site for. */
    ready: initialized,
    hasSite: Boolean(activeSiteId),
    activeSiteId,
    /** No site AND none to choose — a provisioning problem, not a user error. */
    hasNoSitesAtAll: initialized && !isCorpUser && (availableSites || []).length === 0,
    /**
     * Imperative guard for pages that open a create form in a modal.
     * Returns true when it is safe to proceed.
     *
     *   if (!requireSite(showWarning, 'training course')) return;
     */
    requireSite: (showWarning, entityLabel = 'record') => {
      if (activeSiteId) return true;
      if (typeof showWarning === 'function') {
        showWarning(
          `Please select a site from the site selector before adding a ${entityLabel}.`
        );
      }
      return false;
    },
  };
};

/**
 * Declarative guard for full-page create forms. Renders `children` only when a
 * site is active; otherwise an explanatory panel.
 */
const SiteRequiredGate = ({ children, entityLabel = 'record', className = '' }) => {
  const { ready, hasSite, hasNoSitesAtAll } = useSiteRequired();

  // Don't paint anything until site context settles, or single-site users would
  // see the blocker flash before their site is auto-selected.
  if (!ready) return null;
  if (hasSite) return <>{children}</>;

  return (
    <div className={`bg-amber-50 border-2 border-amber-300 rounded-xl p-6 ${className}`}>
      <h3 className="text-lg font-bold text-amber-900 mb-2">Select a site to continue</h3>
      {hasNoSitesAtAll ? (
        <p className="text-sm text-amber-800">
          You are not assigned to any site yet, so a new {entityLabel} cannot be
          created. Please ask an administrator to assign you to a site.
        </p>
      ) : (
        <p className="text-sm text-amber-800">
          A new {entityLabel} belongs to a specific site. Choose one using the site
          selector in the header, then this form will open — that way nothing you
          type is lost to a failed save.
        </p>
      )}
    </div>
  );
};

export default SiteRequiredGate;
